import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { from, of } from 'rxjs'
import { catchError, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators'
import { ShipmentRateApiService } from 'src/api/shipment-routing/shipment-rate'
import { FreightRatesService } from 'src/pages/admin/logistics/freight-rates/freight-rates.service'
import { chunked, queueMap } from 'src/services/data/utils'
import { dayjs } from 'src/services/dayjs'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { catchAndLog } from 'src/shared/utils/catch-and-log'
import {
  archiveShipmentRates,
  cloneShipmentRates, cloneShipmentRatesFailure, cloneShipmentRatesSuccess,
  createShipmentRate, createShipmentRateFailure, createShipmentRateSuccess,
  expireShipmentRates,
  flagShipmentRate, flagShipmentRateFailure, flagShipmentRateSuccess,
  loadShipmentRate, loadShipmentRateFailure, loadShipmentRateSuccess,
  loadShipmentRatesByIds, loadShipmentRatesByIdsFailure, loadShipmentRatesByIdsSuccess,
  loadShipmentRates, loadShipmentRatesFailure, loadShipmentRatesSuccess,
  unarchiveShipmentRates,
  updateShipmentRate, updateShipmentRateFailure, updateShipmentRateSuccess,
  updateShipmentRatesSuccess,
  loadShipmentRatesTable, loadShipmentRatesTableFailure, loadShipmentRatesTableSuccess,
  loadShipmentRateTableFilters, loadShipmentRateTableFiltersFailure,loadShipmentRateTableFiltersSuccess
} from './shipment-rate.actions'
import { TOP_LEVEL_FILTERS } from 'src/pages/admin/logistics/freight-rates/freight-rates-list/freight-rates-list.filters'
import { isEqual } from 'lodash-es'
import { ShipmentRatesElasticSearchService } from 'src/pages/admin/logistics/freight-rates/shipment-rates-search.service'
import { prepareFiltersPayload, prepareShipmentRatePayload } from './shipment-rates-filters.utils'

@Injectable()
export class ShipmentRateEffects {
  private readonly actions$ = inject(Actions)
  private readonly toaster = inject(ToasterService)
  private readonly elastic = inject(ShipmentRatesElasticSearchService)
  private readonly FreightRates = inject(FreightRatesService)
  private readonly ShipmentRateApi = inject(ShipmentRateApiService)


  loadShipmentRate$ = createEffect(() => this.actions$.pipe(
    ofType(loadShipmentRate),
    switchMap(
      (action) => from(this.FreightRates.get(action?.rate_id)).pipe(
        map(({ data }) => loadShipmentRateSuccess({ rate: data })),
        catchAndLog(this.toaster, 'Unable to fetch shipment rate', loadShipmentRateFailure)
      ))
    )
  )

  loadShipmentRatesByIds$ = createEffect(() => this.actions$.pipe(
    ofType(loadShipmentRatesByIds),
    switchMap(
      (action) => from(Promise.all(action.rate_ids.map(rate_id => this.FreightRates.get(rate_id)))).pipe(
        map(
          (results) => loadShipmentRatesByIdsSuccess({ rates: results.map(x => x.data)}),
        ) ,
        catchAndLog(this.toaster, 'Unable to fetch shipment rates', loadShipmentRatesByIdsFailure)
      ))
    )
  )

  loadShipmentRates$ = createEffect(() => this.actions$.pipe(
    ofType(loadShipmentRates),
    switchMap(() =>
      from(this.FreightRates.listAll()).pipe(
        map(rates => loadShipmentRatesSuccess({ rates })),
        catchAndLog(this.toaster, 'Unable to fetch shipment rates', loadShipmentRatesFailure)))))

  createShipmentRate$ = createEffect(() => this.actions$.pipe(
    ofType(createShipmentRate),
    tap(() => this.toaster.progress('Creating shipment rate...')),
    switchMap(action =>
      from(this.FreightRates.create(action.rate)).pipe(
        map(rate => createShipmentRateSuccess({ rate })),
        tap(() => this.toaster.success('Shipment rate created successfully.')),
        catchAndLog(this.toaster, 'Unable to create shipment rate', createShipmentRateFailure)))))

  cloneShipmentRates$ = createEffect(() => this.actions$.pipe(
    ofType(cloneShipmentRates),
    tap(() => this.toaster.progress('Changing shipment rates...')),
    switchMap(action =>
      this.ShipmentRateApi.clone(action.payload).pipe(
        map(({ data: rates }) => cloneShipmentRatesSuccess({ rates })),
        tap(() => this.toaster.success('Shipment rates changed successfully.')),
        catchAndLog(this.toaster, 'Unable to change shipment rates', cloneShipmentRatesFailure)))))

  updateShipmentRate$ = createEffect(() => this.actions$.pipe(
    ofType(updateShipmentRate),
    tap(() => this.toaster.progress('Updating shipment rate...')),
    switchMap(action =>
      from(this.FreightRates.update({ ...action.rate, rate_id: action.id })).pipe(
        map(rate => updateShipmentRateSuccess({ rate })),
        tap(() => this.toaster.success('Shipment rate updated successfully.')),
        catchAndLog(this.toaster, 'Unable to update shipment rate', updateShipmentRateFailure)))))

  flagShipmentRate$ = createEffect(() => this.actions$.pipe(
    ofType(flagShipmentRate),
    tap(() => this.toaster.progress('Saving shipment rate problem...')),
    switchMap(action =>
      from(this.FreightRates.update({ rate_id: action.id, problem: action.problem })).pipe(
        map(rate => flagShipmentRateSuccess({ rate })),
        tap(() => this.toaster.success('Shipment rate problem saved successfully.')),
        catchAndLog(this.toaster, 'Unable to save shipment rate problem', flagShipmentRateFailure)))))

  archiveShipmentRates$ = createEffect(() => this.actions$.pipe(
    ofType(archiveShipmentRates),
    tap(() => this.toaster.progress('Archiving shipment rates...')),
    switchMap(action =>
      from(chunked(action.ids, 200, rateIds =>
        Promise.all(rateIds.map((rate_id) => this.FreightRates.update({ rate_id, archived: true }))))
      ).pipe(
        map(rates => updateShipmentRatesSuccess({ rates })),
        tap(() => this.toaster.success('Shipment rates archived successfully.')),
        catchAndLog(this.toaster, 'Unable to archive shipment rates', updateShipmentRateFailure)))))

  expireShipmentRates$ = createEffect(() => this.actions$.pipe(
    ofType(expireShipmentRates),
    tap(() => this.toaster.progress('Expiring shipment rates...')),
    switchMap(action => {
      const until = dayjs.utc().unix()
      return from(
        this.FreightRates.bulkExpire(action.ids)
      ).pipe(
        map(rates => updateShipmentRatesSuccess({ rates })),
        tap(() => this.toaster.success(`The expiry date of all selected rate(s) have been changed to today at ${dayjs(until).format('hh:mma')}.`)),
        catchAndLog(this.toaster, 'Unable to expire shipment rates', updateShipmentRateFailure))
    })))

  unarchiveShipmentRates$ = createEffect(() => this.actions$.pipe(
    ofType(unarchiveShipmentRates),
    tap(() => this.toaster.progress('Unarchiving rate...')),
    switchMap(action =>
      from(chunked(action.ids, 200, rateIds =>
        Promise.all(rateIds.map((rate_id) => this.FreightRates.update({ rate_id, archived: false }))))
      ).pipe(map(rates => updateShipmentRatesSuccess({ rates })),
        tap(() => this.toaster.success('Shipment rate unarchived successfully.')),
        catchAndLog(this.toaster, 'Unable to unarchive shipment rate', updateShipmentRateFailure)))))

  loadShipmentRateTableFilters$ = createEffect(() => this.actions$.pipe(
    ofType(loadShipmentRateTableFilters),
    map(action => ({
      tableKey: action.tableKey,
      payload: prepareFiltersPayload(action.filters, TOP_LEVEL_FILTERS[action.tableKey]),
    })),
    distinctUntilChanged(isEqual),
    switchMap(({tableKey, payload}) => this.elastic.filters(payload).pipe(
      map(result => loadShipmentRateTableFiltersSuccess({ tableKey, result })),
      catchError(error => of(loadShipmentRateTableFiltersFailure({ error }))),
    )))
  )

  loadShipmentRatesTable$ = createEffect(() => this.actions$.pipe(
      ofType(loadShipmentRatesTable),
      map(action => ({
        action,
        payload: prepareShipmentRatePayload(action.filters, action.page),
      })),
      queueMap(({action, payload}) =>
        this.elastic.search({ ...payload, calculate: action.calculate }).pipe(
          map(r => loadShipmentRatesTableSuccess({
            tableKey: action.tableKey,
            total: r.total,
            shipmentRates: r.data,
            payload,
            totals: {}
          })),
          catchAndLog(this.toaster, 'Unable to fetch shipment rates', loadShipmentRatesTableFailure)
        )
      )
    ))
}
